import { useMemo, useEffect, useRef } from "react";

import { useSelector, useDispatch } from "react-redux";

import { tableActionsCreator } from "app/actions/tableActions";
import MultiColorSelectionTable from "app/components/React/Table/MultiColorSelectionTable";
import { TableWrapper } from "app/pages/conversion/conversionTableContainer/StyledComponents";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import type { RootState } from "single-spa/store/types";

import { industryAnalysisGeoTableColumnsConfigGen } from "../config/IndustryAnalysisGeoTableConfig";
import type { useProcessedGeographyTableData } from "../hooks/useProcessedGeographyTableData";
import { generateTableExcelApiUrl } from "../utils/utils";

import { TableSearchAndDownloadHeader } from "./TableSearchAndDownloadHeader";

type TableDataState = ReturnType<typeof useProcessedGeographyTableData>;

interface IIndustryAnalysisGeographyTableContainerProps {
    tableDataState: TableDataState;
}

export const IndustryAnalysisGeographyTableContainer = ({
    tableDataState,
}: IIndustryAnalysisGeographyTableContainerProps) => {
    const { isLoading, processedData: filteredData, searchString, onSearch, sortedColumn, onSort } = tableDataState;

    const dispatch = useDispatch();
    const selectedRows = useSelector((state: RootState) => getTableSelectedItems("IndustryAnalysisGeoTable")(state));
    const tableExcelUrl = generateTableExcelApiUrl();

    // Table configuration constants
    const tableSelectionKey = "IndustryAnalysisGeoTable";
    const tableSelectionProperty = "Country";

    // Track if we've already performed initial selection to avoid re-selecting when user clears
    const hasInitialized = useRef(false);

    // Track if user has ever made a selection (including initial auto-selection)
    const hasExistingSelection = useMemo(() => {
        return selectedRows && selectedRows.length > 0;
    }, [selectedRows]);

    // Only show selected status for items that are both in the selection and in the filtered data
    const filteredSelectedRows = useMemo(() => {
        if (!filteredData || !selectedRows) {
            return [];
        }
        const filteredSet = new Set(filteredData.map((item) => item[tableSelectionProperty]));
        return selectedRows.filter((sel) => filteredSet.has(sel));
    }, [filteredData, selectedRows, tableSelectionProperty]);

    // Initialize selection with first 7 countries if no selection exists
    useEffect(() => {
        if (
            !isLoading &&
            filteredData?.length > 0 &&
            !hasInitialized.current &&
            (!selectedRows || selectedRows.length === 0)
        ) {
            const tableActions = tableActionsCreator(tableSelectionKey, tableSelectionProperty);
            const firstSevenCountries = filteredData.slice(0, 7);
            dispatch(tableActions.selectRows(firstSevenCountries));
            hasInitialized.current = true;
        }
    }, [dispatch, isLoading, filteredData, selectedRows, tableSelectionKey, tableSelectionProperty]);

    const tableProps = {
        onSort,
        tableOptions: { tableSelectionTrackingParam: tableSelectionProperty },
        isLoading,
        tableData: { Data: filteredData }, // eslint-disable-line @typescript-eslint/naming-convention
        tableColumns: industryAnalysisGeoTableColumnsConfigGen(sortedColumn.field, sortedColumn.sortDirection),
    };

    return (
        <TableWrapper loading={isLoading}>
            <TableSearchAndDownloadHeader
                searchValue={searchString}
                onSearch={onSearch}
                downloadUrl={tableExcelUrl}
                showExcelDownload={true}
            />
            <MultiColorSelectionTable
                key={tableSelectionKey}
                selectedRows={hasExistingSelection ? filteredSelectedRows : selectedRows}
                tableSelectionKey={tableSelectionKey}
                tableSelectionProperty={tableSelectionProperty}
                tableData={filteredData}
                tableProps={tableProps}
            />
        </TableWrapper>
    );
};
